import { inject } from '@angular/core'
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router'

import { catchError, defaultIfEmpty, map, of, switchMap, take } from 'rxjs'

import { AuthService } from '../services/auth.service'
import {
  KpiDashboardService,
  KpiDashboardSummary,
} from '../services/kpi-dashboard/kpi-dashboard.service'
import { UserInfo } from '../services/user/user.service'
import { UserStore } from '../stores/user.store'
import { hasManagementRole } from './kpi-dashboard-access'

interface KpiDashboardGuardContext {
  dashboards: KpiDashboardSummary[]
  user: UserInfo
}

function getGuardContext() {
  const authService = inject(AuthService)
  const userStore = inject(UserStore)
  const kpiDashboardService = inject(KpiDashboardService)

  return authService.user$.pipe(
    // user$（authState 由来）は complete しないため、有限化しないと下の
    // defaultIfEmpty が発火せずガードが無反応のまま止まる
    take(1),
    switchMap(() =>
      userStore.getOneSelf({ useCache: true }).pipe(
        switchMap((user) =>
          kpiDashboardService.dashboards().pipe(
            map(
              (dashboards): KpiDashboardGuardContext => ({
                dashboards,
                user,
              }),
            ),
          ),
        ),
      ),
    ),
    // getOneSelf は非 CustomError 時に値なしで complete するため、空完了を null に
    // 変換してガード側で /home へ倒す（EmptyError → NavigationError の防止）。
    // user$ 側の空完了もカバーできるよう最外側に置く
    defaultIfEmpty(null),
  )
}

function redirectHome(router: Router) {
  router.navigate(['/home'])
  return of(false)
}

function guardManagementDashboardRoute(
  dashboards: KpiDashboardSummary[],
  routeDashboardId: string | null,
  router: Router,
) {
  if (
    routeDashboardId == null ||
    dashboards.some((dashboard) => dashboard.id === routeDashboardId)
  ) {
    return of(true)
  }

  router.navigate(['/kpi-dashboards'])
  return of(false)
}

/**
 * USER ロールのリダイレクト先となる本人の個人画面パスを返す。
 * メンバー外の dashboardId が URL 指定された場合は、アクセス可能な先頭ボードへ倒す。
 */
function ownOperatorDetailPath(
  dashboards: KpiDashboardSummary[],
  user: UserInfo,
  routeDashboardId: string | null,
): string[] {
  const dashboardId =
    routeDashboardId != null &&
    dashboards.some((dashboard) => dashboard.id === routeDashboardId)
      ? routeDashboardId
      : dashboards[0].id
  return ['/kpi-dashboards', dashboardId, 'operators', user.id]
}

export const kpiDashboardGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
) => {
  const router = inject(Router)

  return getGuardContext().pipe(
    switchMap((context) => {
      if (context == null) {
        return redirectHome(router)
      }

      const { dashboards, user } = context
      if (dashboards.length === 0) {
        return redirectHome(router)
      }

      if (hasManagementRole(user)) {
        return guardManagementDashboardRoute(
          dashboards,
          route.paramMap.get('dashboardId'),
          router,
        )
      }

      router.navigate(
        ownOperatorDetailPath(
          dashboards,
          user,
          route.paramMap.get('dashboardId'),
        ),
      )
      return of(false)
    }),
    catchError(() => {
      // ガードに必要な情報の取得に失敗した場合はナビゲーションを止めず /home へ倒す
      return redirectHome(router)
    }),
  )
}

export const kpiOperatorDetailGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
) => {
  const router = inject(Router)

  return getGuardContext().pipe(
    switchMap((context) => {
      if (context == null) {
        return redirectHome(router)
      }

      const { dashboards, user } = context
      if (dashboards.length === 0) {
        return redirectHome(router)
      }

      if (hasManagementRole(user)) {
        return guardManagementDashboardRoute(
          dashboards,
          route.paramMap.get('dashboardId'),
          router,
        )
      }

      // USER は「自分がメンバーのダッシュボード × 自分の userId」のみ許可する
      const routeDashboardId = route.paramMap.get('dashboardId')
      const isAccessibleDashboard =
        routeDashboardId != null &&
        dashboards.some((dashboard) => dashboard.id === routeDashboardId)
      if (isAccessibleDashboard && route.paramMap.get('userId') === user.id) {
        return of(true)
      }

      router.navigate(ownOperatorDetailPath(dashboards, user, routeDashboardId))
      return of(false)
    }),
    catchError(() => {
      // ガードに必要な情報の取得に失敗した場合はナビゲーションを止めず /home へ倒す
      return redirectHome(router)
    }),
  )
}
